import { Box, Button, Tooltip } from "@drone-js/core";
import type { FunctionComponent } from "react";
import { usePropertySelectionViewModelValue } from "../propertySelectionViewModelAtom";
import { BasePropertyListing, type BasePropertyListingProps } from "./BasePropertyListing";
import { ApiPropertyStatus, type BasePropertyList } from "./types";

export interface ImportedListingProps extends BasePropertyList {
    status: ApiPropertyStatus;
    url?: string;
}

export const ImportedListing: FunctionComponent<ImportedListingProps> = ({ id, url, status, ...rest }) => {
    const {
        automaticPropertySelectionCms: {
            active,
            activeTooltip,
            publishing,
            publishingTooltipBeingReviewed,
            publishingTooltipPleaseWait,
            listingIncomplete,
        },
    } = usePropertySelectionViewModelValue();

    const getBadgeProps = (): BasePropertyListingProps["badge"] => {
        if (url) {
            return {
                label: active,
                variant: "tertiary.positive",
            };
        }

        if (!url && status === ApiPropertyStatus.Draft) {
            return {
                label: listingIncomplete,
                variant: "tertiary.urgency",
            };
        }

        return {
            label: publishing,
            variant: "tertiary.notice",
            tooltip: {
                title: publishing,
                content: `${publishingTooltipBeingReviewed} ${publishingTooltipPleaseWait}`,
            },
            analytics: {
                dataElementName: "imported-publishing-badge",
                propertyId: id,
            },
        };
    };

    return (
        <BasePropertyListing badge={getBadgeProps()} childrenPosition={url ? "after" : "before"} {...rest}>
            {url ? (
                <Tooltip content={activeTooltip}>
                    <Button
                        hierarchy="tertiary"
                        size="s"
                        variant="icon"
                        leadingIcon="fill.arrow.share"
                        aria-label="external-link"
                        href={url}
                        data-element-name="imported-external-link"
                        data-property-id={id}
                    />
                </Tooltip>
            ) : (
                <Box width="28px" />
            )}
        </BasePropertyListing>
    );
};
